import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { fetchStates, fetchCitiesByState } from "@/lib/ibge";

interface IBGEState {
  id: number;
  sigla: string;
  nome: string;
}

interface IBGECity {
  id: number;
  nome: string;
}

interface StateCitySelectProps {
  selectedState: string | undefined;
  onStateChange: (stateUf: string | undefined) => void;
  selectedCity: string | undefined;
  onCityChange: (cityName: string | undefined) => void;
  disabled?: boolean;
  hideLabels?: boolean;
  statePlaceholder?: string;
  cityPlaceholder?: string;
}

const StateCitySelect: React.FC<StateCitySelectProps> = ({
  selectedState,
  onStateChange,
  selectedCity,
  onCityChange,
  disabled = false,
  hideLabels = false,
  statePlaceholder = "Selecione o Estado",
  cityPlaceholder = "Selecione a Cidade",
}) => {
  const { data: states, isLoading: isLoadingStates } = useQuery<IBGEState[]>({
    queryKey: ["ibgeStates"],
    queryFn: fetchStates,
    staleTime: Infinity,
  });

  const { data: cities, isLoading: isLoadingCities } = useQuery<IBGECity[]>({
    queryKey: ["ibgeCities", selectedState],
    queryFn: () => (selectedState ? fetchCitiesByState(selectedState) : Promise.resolve([])),
    enabled: !!selectedState,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (selectedState && cities && selectedCity) {
      const normalizedSelected = selectedCity.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
      const matchedCity = cities.find(city => city.nome.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() === normalizedSelected);
      
      if (matchedCity && matchedCity.nome !== selectedCity) {
        onCityChange(matchedCity.nome);
      } else if (!matchedCity) {
        onCityChange(undefined);
      }
    }
  }, [selectedState, cities, selectedCity, onCityChange]);

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div>
        {!hideLabels && <Label htmlFor="state-select">Estado (UF)</Label>}
        <Select
          value={selectedState}
          onValueChange={(value) => {
            onStateChange(value);
            onCityChange(undefined);
          }}
          disabled={disabled || isLoadingStates}
        >
          <SelectTrigger id="state-select" aria-label="Selecionar estado" className="text-black">
            {isLoadingStates ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <SelectValue placeholder={statePlaceholder} />
            )}
          </SelectTrigger>
          <SelectContent>
            {states?.map((state) => (
              <SelectItem key={state.id} value={state.sigla}>
                {state.nome} ({state.sigla})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        {!hideLabels && <Label htmlFor="city-select">Cidade</Label>}
        <Select
          value={selectedCity}
          onValueChange={onCityChange}
          disabled={disabled || !selectedState || isLoadingCities}
        >
          <SelectTrigger id="city-select" aria-label="Selecionar cidade" className="text-black">
            {isLoadingCities ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <SelectValue placeholder={cityPlaceholder} />
            )}
          </SelectTrigger>
          <SelectContent>
            {cities?.map((city) => (
              <SelectItem key={city.id} value={city.nome}>
                {city.nome}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default StateCitySelect;